<template>

    <div id="donation-form" class="mb-10 sm-mb-5">

        <template v-if="!loading">

            <template v-if="showBlock">

                <h3
                    v-if="isFinalStep"
                    class="text-center mb-4 sm-mb-3">Thanks for your donation to {{ title }}</h3>

                <template v-else>
                    <!-- TITLE -->
                    <h3
                        v-if="title"
                        class="text-center mb-4 sm-mb-3">{{ blockTitle }}</h3>

                    <!-- DONATION TYPE -->
                    <div class="d-flex justify-content-center">
                        <div class="radioOptions">
                            <div
                                v-for="type in donationTypesFiltered"
                                :key="`${type.slug}-${type.id}`"
                                class="radioOptions__wrapper">
                                <label :for="type.slug">
                                    <input type="radio" name="type" :id="type.slug" :value="type.id" class="radioOptions__radio" v-model="payment_type">
                                    <div class="radioOptions__option">
                                        <span class="radioOptions__option-text">{{ type.label }}</span>
                                    </div>
                                </label>
                            </div>
                        </div>
                    </div>
                </template>

                <!-- PROGRESS BAR -->
                <ProgressBar
                    class="mb-5 sm-mb-2"
                    :active="currentStep"
                    :steps="progressBar.steps"
                    @change-step="updateStep"/>

                <transition name="fade">

                    <!-- DONATION OPTIONS -->
                    <DonationOptions
                        v-if="currentStep === 0"
                        @selected="setSelection"/>

                    <!-- DONATION FORM -->
                    <DonationDetails
                        v-else-if="currentStep === 1"
                        :amount="amount"
                        :type="selectedDonationType.label"
                        @completed="updateDetails"/>
                    <!-- DONATION PAYMENT -->
                    <DonationPayment
                        v-else-if="currentStep === 2"
                        :stripe-key="stripeKey"
                        :title="title"
                        :amount="parseFloat(amount)"
                        :amount-description="amount_description"
                        :type="selectedDonationType"
                        :post-meta="post_meta"
                        :monthly-donation-redirect-to-custom-page = "monthlyDonationRedirectToCustomPage"
                        @payment-success="afterPayment"/>

                    <DonationPaymentSuccess
                        v-else-if="currentStep === 3"
                        :type="selectedDonationType.label"
                        :amount="amount"
                        :title="title"
                        :payment="payment"></DonationPaymentSuccess>

                </transition>

            </template>

            <h4 v-else class="text-center mb-8 sm-mb-4">Currently, there are no donation options available.</h4>

        </template>

        <MoonSpinner
            v-else
            :loading="true"
            container-size="160px"/>

    </div>

</template>

<script>

    import {mapGetters, mapActions} from 'vuex';

    import ProgressBar from '@/components/donations/ProgressBar';
    import DonationOptions from '@/components/donations/DonationOptions';
    import DonationDetails from '@/components/donations/DonationDetails';
    import DonationPayment from '@/components/donations/DonationPayment';
    import DonationPaymentSuccess from "@/components/donations/DonationPaymentSuccess";
    import MoonSpinner from "@/components/spinners/MoonSpinner";
    import Cookies from "js-cookie";

    export default {
        name: 'DonationAmounts',
        components: {MoonSpinner, ProgressBar, DonationOptions,DonationDetails,DonationPayment,DonationPaymentSuccess},

        props: {
            postId: {
                type: String,
                required: true
            },
            postType: {
                type: String,
                required: true
            },
            postTitle: {
                type: String,
                required: true
            },
            stripeKey: {
                type: String,
                required: true
            },
            oneOffRedirectToCustomPage: {
                type: String,
                required: false,
                default: false
            },
            monthlyDonationRedirectToCustomPage: {
                type: String,
                required: false,
                default: false
            }
        },

        data() {
            return {
                payment_type: 1, // one-off or regular
                amount: 0,
                amount_description: '',
                payment: {},

                post_meta: {
                    donation_title: '',
                    post_type: this.postType,
                    post_id: this.postId,
                    post_title: this.postTitle
                }
            }
        },

        computed: {
            ...mapGetters('Donations', {
                showBlock: 'hasDonationAmounts',
                block: 'donationOptions',
                loadingOptions: 'fetchingOptions',
                loadingDonationSettings: 'fetchingDonationSettings',
                progressBar: 'progressBar'
            }),

            loading() {
                return !! (
                    this.loadingOptions ||
                    this.loadingDonationSettings
                );
            },

            blockTitle() {
                return this.block
                    ? this.block.title
                    : '';
            },

            title() {
                return this.blockTitle
                    ? this.blockTitle
                    : this.postTitle;
            },

            donationTypes() {
                return (!this.block || !this.block.types)
                        ? false
                        : this.block.types;
            },

            donationTypesFiltered() {
                if (!this.donationTypes) return [];
                return this.donationTypes;
            },

            selectedDonationType() {
                return this.donationTypesFiltered.find((item) => item.id === this.payment_type);
            },

            currentStep() {
                return this.progressBar.current_step;
            },

            finalStep() {
                return 3;
            },

            isFinalStep() {
                return this.currentStep === this.finalStep;
            }
        },

        watch: {
            title: {
                immediate: true,
                handler(val) {
                    this.post_meta.donation_title = val;
                }
            },
            currentStep(val) {
                this.$nextTick(() => {
                    document.getElementById("donation-form").scrollIntoView();
                });
            },
            donationTypes(val) {
                this.payment_type = val.find((item) => item.default).id;
            }
        },

        created() {
            this.loadData();
        },

        methods: {
            ...mapActions('Donations', [
                'donationOptions', 'donationSettings','changeStep', 'resetDonationData', 'syncLastDonation'
            ]),
            loadData() {
                this.donationOptions({postId: this.postId});
                this.donationSettings();
            },
            updateStep(step) {
                this.changeStep({step});
            },
            setSelection({amount, description}) {
                this.amount = amount;
                this.amount_description = description;
                this.updateStep(1);
            },
            updateDetails() {
                this.updateStep(2);
            },
            afterPayment(data) {
                this.payment = data;

                if(this.oneOffRedirectToCustomPage){
                    this.syncLastDonation({amount: data.donation_amount, description: ''});
                    window.location.href = this.oneOffRedirectToCustomPage;
                } else {
                    this.updateStep(3);
                    this.syncLastDonation({amount: data.donation_amount, description: ''});
                }
            }
        }
    }

</script>
